import {
  VStack,
  CustomizedTypography,
  Stack,
  CustomizedButton
} from '@components/Customized';
import { Image } from '@components/Image';
import { makeStyles } from '@material-ui/core';
import {
  useCanEditRefundAccountLazyQuery,
  useDeleteMyRefundAccountByQuotationUserMutation,
  useFetchMyQuotationUserQuery,
  useFetchMyRefundBankAccountsQuery
} from '@generated/graphql';
import { maskingBankAccount } from '@utils/maskingString';
import { CapaBankCodeToBankInfo } from '../BankAccountInputGroup/BankAccountInputGroup';
import { DefaultThumbnail } from '@assets';
import { colorSet, useSnackbar } from '@components/Provider';
import { PreventEditDialog } from '../../Dialog/RequestActionDialog';
import { useState } from 'react';

const RefundAccountList = () => {
  const { editButton, bankAccountFont } = useStyles();
  const { data: myQuotationUser } = useFetchMyQuotationUserQuery({
    fetchPolicy: 'cache-first'
  });
  const { enqueueSnackbar } = useSnackbar();
  const [
    canEditRefundAccount,
    { loading: canEditRefundLoading }
  ] = useCanEditRefundAccountLazyQuery({
    fetchPolicy: 'network-only'
  });

  const [
    deleteMyRefundAccountByQuotationUser,
    { loading: deleteMyRefundAccountLoading }
  ] = useDeleteMyRefundAccountByQuotationUserMutation({
    refetchQueries: ['fetchMyRefundBankAccounts']
  });

  const [preventEditModalOpen, setPreventEditModalOpen] = useState(false);

  const { data: myRefundBankAccounts } = useFetchMyRefundBankAccountsQuery();

  const name =
    myQuotationUser?.fetchMyQuotationUser.data?.verificationName ?? '';
  const bankInfos = myRefundBankAccounts?.fetchMyRefundBankAccounts.data ?? [];

  const handleEditButton = async (refundBankAccountId: string) => {
    try {
      const { data: canEditRefundAccountData } = await canEditRefundAccount();
      const canEdit =
        canEditRefundAccountData?.canEditRefundAccount.data
          ?.canEditRefundAccount ?? false;

      if (!canEdit) {
        setPreventEditModalOpen(true);
        return;
      }
      deleteMyRefundAccountByQuotationUser({
        variables: {
          input: {
            refundBankAccountId
          }
        }
      });
    } catch (error) {
      enqueueSnackbar('수정 요청에 실패했습니다', {
        variant: 'error'
      });
    }
  };

  return (
    <VStack>
      <CustomizedTypography variant="body1" gutterBottoms={12}>
        환불계좌
      </CustomizedTypography>

      {bankInfos.map((bankInfo) => (
        <Stack justifyContent="space-between" key={bankInfo.id}>
          <Stack gap={8}>
            <Image
              width={44}
              height={44}
              src={
                CapaBankCodeToBankInfo[bankInfo.bankCode]?.logo ??
                DefaultThumbnail
              }
            />
            <VStack>
              <CustomizedTypography>{name}</CustomizedTypography>
              <CustomizedTypography>
                {bankInfo.holderName}
                <em className={bankAccountFont}>
                  {maskingBankAccount(bankInfo.account)}
                </em>
              </CustomizedTypography>
            </VStack>
          </Stack>

          <CustomizedButton
            data-testid="refundAccountForm-delete"
            className={editButton}
            variant="contained"
            color="dark"
            disabled={deleteMyRefundAccountLoading || canEditRefundLoading}
            onClick={() => handleEditButton(bankInfo.id)}
          >
            계좌삭제
          </CustomizedButton>
        </Stack>
      ))}
      <PreventEditDialog
        open={preventEditModalOpen}
        onClose={() => setPreventEditModalOpen(false)}
      />
    </VStack>
  );
};

export default RefundAccountList;

const useStyles = makeStyles(
  (theme) => ({
    editButton: {
      minWidth: '84px !important',
      width: 84
    },
    bankAccountFont: {
      marginLeft: 4,
      color: colorSet.gray700
    }
  }),
  { classNamePrefix: 'RefundAccountList-' }
);
